import React, { useState, useEffect, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Trophy, Vote, Activity, Crown, Medal, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ContestVoteButton } from '../../components/contest/ContestVoteButton';
import { supabase } from '../../lib/supabase';
import { LazyImage } from '../../components/shared/LazyImage';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getOptimizedImageUrl } from '../../lib/imageUtils';

interface Contestant {
  id: string;
  name: string;
  unique_code: string;
  votes_count: number;
  contest_name: string;
  contest_slug: string;
  photo_url?: string;
}

interface Contest {
  id: string;
  name: string;
  slug: string;
  top_contestants: Contestant[];
}

export const Classement = () => {
  const { data: globalData, isLoading: isLoadingGlobal } = useQuery({
    queryKey: ['globalRanking'],
    queryFn: async () => {
      // Appel direct à la fonction optimisée Supabase
      const { data, error } = await supabase.rpc('get_top_contestants_v2');
      if (error) throw error;
      if (!data) return { topGlobalContestants: [], contests: [] };

      // Top 10 global
      const topGlobalContestants = data.slice(0, 10).map((item: any) => ({
        id: item.contestant_id,
        name: item.contestant_name,
        unique_code: item.unique_code,
        votes_count: Number(item.votes_count),
        contest_name: item.contest_name,
        contest_slug: item.contest_slug,
        photo_url: item.photo_url,
      }));

      // Regrouper par concours pour top 3 par concours
      const contestsMap: Record<string, Contest> = {};
      data.forEach((item: any) => {
        if (!contestsMap[item.contest_id]) {
          contestsMap[item.contest_id] = {
            id: item.contest_id,
            name: item.contest_name,
            slug: item.contest_slug,
            top_contestants: [],
          };
        }
        contestsMap[item.contest_id].top_contestants.push({
          id: item.contestant_id,
          name: item.contestant_name,
          unique_code: item.unique_code,
          votes_count: Number(item.votes_count),
          contest_name: item.contest_name,
          contest_slug: item.contest_slug,
          photo_url: item.photo_url,
        });
      });
      // Garder seulement le top 3 pour chaque concours
      const contests = Object.values(contestsMap).map(contest => ({
        ...contest,
        top_contestants: contest.top_contestants.slice(0, 3)
      }));

      return {
        topGlobalContestants,
        contests: contests.filter(c => c.top_contestants.length > 0),
      };
    },
    refetchInterval: 30000,
    staleTime: 15000,
  });

  if (isLoadingGlobal) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-yellow-400 mx-auto mb-4" />
            <p className="text-gray-400">Chargement du classement...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-white" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-300" />;
      case 3:
        return <Medal className="h-6 w-6 text-yellow-600" />;
      default:
        return <Trophy className="h-6 w-6 text-white" />;
    }
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Classement ERNEST NZOGOUG - Top des Concours et Candidats"
        description="Découvrez le classement en direct des meilleurs candidats sur ERNEST NZOGOUG. Votez pour vos favoris et suivez leur progression dans nos concours prestigieux."
        image="https://nzofoug.com/leaderboard-banner.jpg"
        url="https://nzofoug.com/classement"
      />

      <style global>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes text-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        
        .animate-text-shimmer {
          animation: text-shimmer 4s ease-in-out infinite;
          background-size: 200% auto;
        }

        .luxurious-card {
          position: relative;
          background: linear-gradient(to bottom right, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.7));
          backdrop-filter: blur(10px);
        }

        .luxurious-card::before {
          content: '';
          position: absolute;
          inset: -1px;
          background: linear-gradient(45deg, 
            rgba(234, 179, 8, 0.3),
            rgba(234, 179, 8, 0.1),
            rgba(234, 179, 8, 0.3)
          );
          background-size: 200% 200%;
          animation: shine 3s linear infinite;
          border-radius: inherit;
          z-index: -1;
        }

        @keyframes gradient-x {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient-x {
          background-size: 200% 100%;
          animation: gradient-x 4s ease infinite;
        }
      `}</style>

      <div className="min-h-screen relative">
        {/* Background Image avec Overlay et Parallaxe */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        {/* Image de fond secondaire avec motif */}
        <div 
          className="fixed inset-0 z-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px'
          }}
        ></div>

        {/* Contenu principal */}
        <div className="relative z-10">
          {/* Top 10 Global */}
          <section className="relative pt-32 pb-24">
            <div className="relative container mx-auto px-4">
              <div className="text-center mb-16">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
                >
                  <Crown className="h-5 w-5 text-white" />
                  <span className="text-white font-medium">Top 10 Global</span>
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 bg-clip-text text-transparent animate-gradient-x"
                >
                  Classement Global
                </motion.h1>
                <p className="text-xl text-gray-300 mb-12">
                  Les candidats qui se distinguent par leur popularité exceptionnelle sur ERNEST NZOGOUG
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
                {globalData?.topGlobalContestants.map((contestant: Contestant, index: number) => (
                  <motion.div
                    key={contestant.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="group relative"
                  >
                    <div className="absolute -inset-1 bg-white/10 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
                    <div className="relative bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full flex flex-col items-center text-center overflow-hidden">
                      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.2)] flex items-center gap-2">
                        {getRankIcon(index + 1)}
                        <span className="text-white text-sm font-bold">#{index + 1}</span>
                      </div>

                      <Link
                        to={`/${contestant.unique_code}`}
                        className="relative block w-40 h-40 mb-8 transform group-hover:scale-110 transition-all duration-500"
                      >
                        <LazyImage
                          src={getOptimizedImageUrl(contestant.photo_url, 128, 128)}
                          alt={contestant.name}
                          className="w-full h-full object-cover rounded-full border-2 border-white/30 group-hover:border-white/50 transition-colors duration-500"
                        />
                      </Link>

                      <Link
                        to={`/${contestant.unique_code}`}
                        className="text-xl font-bold text-white mb-2 hover:text-gray-300 transition-colors"
                      >
                        {contestant.name}
                      </Link>

                      <Link
                        to={`/c/${contestant.contest_slug}`}
                        className="text-sm text-gray-400 mb-4 hover:text-white transition-colors"
                      >
                        {contestant.contest_name}
                      </Link>

                      <div className="mb-6">
                        <ContestVoteButton uniqueCode={contestant.unique_code} />
                      </div>

                      <div className="w-full space-y-3">
                        <div className="group-hover:transform group-hover:scale-105 transition-all duration-300">
                          <div className="flex items-center justify-center gap-2 text-white mb-1">
                            <Vote className="h-4 w-4" />
                            <span className="font-medium">{contestant.votes_count} votes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Top 3 par Concours */}
          <Suspense fallback={<div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-yellow-400 mx-auto" /></div>}>
            <section className="relative py-16">
              <div className="relative container mx-auto px-4">
                <div className="text-center mb-12">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
                  >
                    <Trophy className="h-5 w-5 text-white" />
                    <span className="text-white font-medium">Classements par Concours</span>
                  </motion.div>
                  <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 bg-clip-text text-transparent animate-gradient-x">
                    Top 3 par Catégorie
                  </h2>
                  <p className="text-gray-400 max-w-2xl mx-auto">
                    Découvrez les candidats les mieux classés dans chaque concours
                  </p>
                </div>

                <div className="grid gap-6">
                  {globalData?.contests.map((contest: Contest) => (
                    <motion.div
                      key={contest.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="relative group"
                    >
                      <div className="absolute -inset-2 bg-white/5 rounded-xl blur-md opacity-25 group-hover:opacity-50 transition duration-300"></div>
                      <div className="relative bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl p-6">
                        {/* En-tête du concours */}
                        <div className="flex items-center justify-between mb-6">
                          <Link
                            to={`/c/${contest.slug}`}
                            className="group/title"
                          >
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 bg-clip-text text-transparent group-hover/title:from-yellow-100 group-hover/title:via-yellow-300 group-hover/title:to-yellow-100 transition-all duration-300">
                              {contest.name}
                            </h3>
                          </Link>
                          <Link
                            to={`/c/${contest.slug}`}
                            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                          >
                            Voir tous les candidats
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>

                        {/* Liste des top 3 en ligne */}
                        <div className="flex flex-wrap gap-6">
                          {contest.top_contestants.map((contestant: Contestant, index: number) => (
                            <motion.div
                              key={contestant.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.2, delay: index * 0.05 }}
                              className="flex-1 min-w-[280px] group/card relative"
                            >
                              <div className="flex items-center gap-4">
                                {/* Badge de position */}
                                <div className="shrink-0 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                                  {getRankIcon(index + 1)}
                                </div>

                                {/* Photo du candidat */}
                                <Link
                                  to={`/${contestant.unique_code}`}
                                  className="relative block shrink-0 w-16 h-16 transform group-hover/card:scale-110 transition-all duration-500"
                                >
                                  <LazyImage
                                    src={getOptimizedImageUrl(contestant.photo_url, 128, 128)}
                                    alt={contestant.name}
                                    className="w-full h-full object-cover rounded-full border-2 border-white/30 group-hover/card:border-white/50 transition-colors duration-500"
                                  />
                                </Link>

                                {/* Informations du candidat */}
                                <div className="flex-1 min-w-0">
                                  <Link
                                    to={`/${contestant.unique_code}`}
                                    className="block text-lg font-bold text-white truncate hover:text-gray-300 transition-colors"
                                  >
                                    {contestant.name}
                                  </Link>
                                  <div className="mt-1">
                                    <div className="flex items-center gap-2 text-sm text-white/70">
                                      <Vote className="h-4 w-4" />
                                      <span>{contestant.votes_count} votes</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          </Suspense>

          {/* Ajout de styles dans le head pour l'animation de brillance */}
          <style>{`
            @keyframes gradient-x {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            .animate-gradient-x {
              background-size: 200% 100%;
              animation: gradient-x 4s ease infinite;
            }
          `}</style>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Classement;
